import { t } from 'i18n';
import React, { useCallback, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { Dropdown, DropdownMenu, DropdownToggle } from '@teko/ui';
import { CartNumberTracking } from '@teko/ecommerce';
import { useCartV2, useTenantConfig } from 'contexts';
import { styled, CustomLink, Badge, Body } from '@teko/ds-desktop';
import {
  ShoppingCartOutlined,
  NewLayoutShoppingCartOutlined,
} from '@teko/ds-icon';
import { getTrackingData } from '@teko/tracking';
import {
  NavigationButton,
  NavigationIconButton,
} from 'components/desktop/organisms/style';
import { useRequireLoginForCartAction } from 'hooks';
import { useRouter } from 'next/router';

const MiniCart = dynamic(() =>
  import('./MiniCart').then((mod) => mod.MiniCart),
);
interface CartOverviewProps {
  vertical?: boolean;
  showShippingFeeMiniCart?: boolean;
}
const StyledDropdown = styled(Dropdown)`
  width: auto;
  position: relative;
`;
const WrapperBadge = styled.div`
  position: absolute;
  top: 5px;
  left: calc(50% + 15px);
`;

const NewLayoutWrapperBadge = styled.div`
  position: absolute;
  top: 2px;
  left: calc(50% + 10px);
`;

export const CartOverview: React.FC<CartOverviewProps> = ({
  vertical = false,
  showShippingFeeMiniCart = true,
}: CartOverviewProps) => {
  const { flattenItems, totalCartItems } = useCartV2();
  const [miniCartOpen, setMiniCartOpen] = useState(false);
  const { displayNewLayout } = useTenantConfig();
  const requireLogin = useRequireLoginForCartAction();

  const toggleMiniCart = useCallback(() => {
    setMiniCartOpen(!miniCartOpen);
  }, [miniCartOpen]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (miniCartOpen) {
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${getScrollbarWidth()}px`;
      } else {
        document.body.style.overflow = 'unset';
        document.body.style.paddingRight = 'unset';
      }
    }
  }, [miniCartOpen]);

  const router = useRouter();
  const { query } = router;

  // hide MiniCart with BuyNow screen
  if (!!query.bnk) {
    return null;
  }

  return (
    <StyledDropdown
      isOpen={miniCartOpen}
      toggle={toggleMiniCart}
      toggleType="hover"
      onHide={(): void => setMiniCartOpen(false)}
      {...getTrackingData({
        isTrackContent: true,
        regionName: 'headerBar',
        contentName: 'cartItem',
        target: 'shoppingCart',
      })}
    >
      <DropdownToggle>
        <CustomLink
          href="/cart"
          onClick={(e) => {
            if (requireLogin()) e.preventDefault();
          }}
        >
          {displayNewLayout ? (
            <NavigationIconButton>
              <NewLayoutShoppingCartOutlined size={20} color="black" />
              {totalCartItems > 0 && (
                <NewLayoutWrapperBadge>
                  <Badge count={totalCartItems} />
                </NewLayoutWrapperBadge>
              )}
            </NavigationIconButton>
          ) : (
            <>
              {vertical ? (
                <NavigationButton
                  className="button"
                  style={{ flexDirection: 'column' }}
                >
                  <ShoppingCartOutlined size={36} color="textSecondary" />
                  <Body
                    level={2}
                    weight="medium"
                    color="textSecondary"
                    className="title"
                  >
                    {t('Giỏ hàng')}
                  </Body>
                  {totalCartItems > 0 && (
                    <WrapperBadge>
                      <Badge count={totalCartItems} />
                    </WrapperBadge>
                  )}
                </NavigationButton>
              ) : (
                <NavigationButton>
                  <ShoppingCartOutlined size={36} color="textSecondary" />
                  <div className="ml-6 text-left">
                    <Body
                      level={2}
                      weight="medium"
                      color="textSecondary"
                      className="title"
                    >
                      {t('Giỏ hàng của bạn')}
                    </Body>
                    <Body
                      level={2}
                      weight="medium"
                      color="textSecondary"
                      className="title"
                    >
                      {t('({{cartTotalCartItems}}) sản phẩm', {
                        cartTotalCartItems: flattenItems.length,
                      })}
                    </Body>
                  </div>
                </NavigationButton>
              )}
            </>
          )}
        </CustomLink>
        <CartNumberTracking totalItems={totalCartItems} />
      </DropdownToggle>
      <DropdownMenu popperConfig={{ placement: 'top-end' }}>
        <MiniCart
          onHide={(): void => setMiniCartOpen(false)}
          showShippingFee={showShippingFeeMiniCart}
        />
      </DropdownMenu>
    </StyledDropdown>
  );
};

const getScrollbarWidth = () => {
  let width = 0;
  if (
    typeof window !== 'undefined' &&
    document.body.scrollHeight > window.innerHeight
  ) {
    if (window.scrollbarWidth) {
      return window.scrollbarWidth;
    }
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    document.body.appendChild(outer);
    const inner = document.createElement('div');
    outer.appendChild(inner);
    width = outer.offsetWidth - inner.offsetWidth;
    outer.parentNode?.removeChild(outer);
    window.scrollbarWidth = width;
  }
  return width;
};
